import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LegalPage.css';

const sections = [
    {
        id: 1,
        title: '1. Introduction',
        content: `Bunchy ("we", "our", "us") respects your privacy and is committed to protecting your personal data in accordance with applicable data protection laws, including the General Data Protection Regulation (EU) 2016/679 ("GDPR").

Bunchy is a mobile application designed to connect users through private groups based on shared interests, activities, experiences or life situations, in a secure and non-public environment.

This Privacy Policy explains what data we collect, why we collect it, how it is used, how long it is retained, and what rights you have regarding your personal data.`
    },
    {
        id: 2,
        title: '2. Data We Collect',
        subsections: [
            {
                title: '2.1 Account Information',
                content: 'When you create an account, we collect:',
                bullets: ['Email address', 'Name or pseudonym', 'Profile photo (optional)'],
                note: 'This information is required to create and manage your user account.'
            },
            {
                title: '2.2 Location Information',
                content: 'To enable local connections, we collect:',
                bullets: ['Country', 'Region', 'City', 'Approximate location used solely to connect users within an approximate radius of 25 km'],
                negatives: ['does not collect precise GPS coordinates', 'does not track real-time location', 'does not run background location tracking']
            },
            {
                title: '2.3 Group & Usage Data',
                content: 'We collect data related to your use of the application, including:',
                bullets: ['Groups joined or created', 'Messages and content shared within groups', 'Activities created, joined or interacted with'],
                note: 'This data is processed solely to enable the core functionality of the application.'
            },
            {
                title: '2.4 Payment Information',
                content: 'All payments are processed exclusively by:',
                bullets: ['Apple App Store', 'Google Play Store'],
                negatives: ['does not collect, process or store any payment card, banking or billing information', 'does not have access to full payment details']
            }
        ]
    },
    {
        id: 3,
        title: '3. Legal Basis for Processing',
        content: 'We process personal data on the following legal bases:',
        bullets: [
            'Performance of a contract (providing access to the Bunchy service)',
            'User consent, where applicable',
            'Compliance with legal obligations',
            'Legitimate interests, including service operation, security, moderation and improvement'
        ]
    },
    {
        id: 4,
        title: '4. Sensitive Topics Disclaimer',
        content: 'Some groups on Bunchy may relate to health conditions, behaviors, addictions or personal life situations.\n\nBy joining such groups, users acknowledge that:',
        bullets: ['participation is entirely voluntary', 'information shared is provided by users themselves', 'Bunchy processes this data solely to enable group discussions'],
        negatives: [
            'does not provide medical, psychological or therapeutic advice',
            'does not offer diagnosis or treatment',
            'does not replace professional healthcare or support services'
        ]
    },
    {
        id: 5,
        title: '5. Privacy & Visibility',
        content: 'Bunchy is designed to minimize exposure of personal data:',
        bullets: [
            'All groups are private and accessible only to their members',
            'No public user profiles',
            'No public user directory or search',
            'No private one-to-one messaging',
            'Content shared within a group is visible only to that group\'s members'
        ]
    },
    {
        id: 6,
        title: '6. Data Sharing',
        content: 'Personal data may be shared only:',
        bullets: [
            'with trusted technical service providers (hosting, infrastructure, security), acting as data processors',
            'when legally required by law or authority'
        ],
        negatives: [
            'does not sell personal data',
            'does not share data for advertising or commercial profiling',
            'does not use third-party ad networks'
        ]
    },
    {
        id: 7,
        title: '7. Data Retention',
        content: 'Personal data is retained:',
        bullets: ['only for as long as the user account remains active', 'or as required by applicable legal obligations'],
        note: 'Users may request account deletion and full data deletion. Deletion requests are processed within a reasonable timeframe.'
    },
    {
        id: 8,
        title: '8. Security',
        content: 'We implement appropriate technical and organizational measures to protect personal data against:',
        bullets: ['unauthorized access', 'loss', 'alteration', 'misuse'],
        note: 'These measures include secure hosting, access controls and data minimization principles.'
    },
    {
        id: 9,
        title: '9. User Rights (GDPR)',
        content: 'In accordance with the GDPR, users have the right to:',
        bullets: [
            'access their personal data',
            'rectify inaccurate or incomplete data',
            'request deletion ("right to be forgotten")',
            'restrict or object to processing',
            'withdraw consent at any time',
            'request data portability where applicable'
        ],
        note: 'Requests can be sent to: support@bunchy.io'
    },
    {
        id: 10,
        title: '10. International Data Transfers',
        content: 'If personal data is processed outside the European Economic Area (EEA), appropriate safeguards are implemented in accordance with GDPR requirements.'
    },
    {
        id: 11,
        title: '11. Children & Age Restriction',
        content: 'Bunchy is not intended for users under the age of 18.\nWe do not knowingly collect data from minors.'
    },
    {
        id: 12,
        title: '12. Changes to This Policy',
        content: 'This Privacy Policy may be updated from time to time.\n\nUsers will be informed of significant changes through the application or by appropriate means.'
    }
];

const PrivacyPolicy = () => {
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState(null);

    return (
        <div className="legal-page">
            {/* Header */}
            <header className="legal-header">
                <div className="legal-header-inner">
                    <div className="legal-logo-wrap" onClick={() => navigate('/login')}>
                        <img src="/bunchy-logo.svg" alt="Bunchy" className="legal-logo" />
                        <span className="legal-brand">bunchy</span>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <div className="legal-hero">
                <h1 className="legal-hero-title">Privacy Policy</h1>
            </div>

            {/* Content */}
            <div className="legal-body">
                {/* Sections */}
                <div className="legal-sections">
                    {sections.map(s => (
                        <section
                            key={s.id}
                            id={`section-${s.id}`}
                            className="legal-section"
                        >
                            <h2>{s.title}</h2>

                            {s.content && <p className="legal-para">{s.content}</p>}

                            {s.bullets && (
                                <ul className="legal-bullets">
                                    {s.bullets.map((b, i) => <li key={i}>✅ {b}</li>)}
                                </ul>
                            )}

                            {s.negatives && (
                                <div className="legal-negatives">
                                    <span className="bunchy-label">Bunchy:</span>
                                    <ul>
                                        {s.negatives.map((n, i) => <li key={i}>❌ {n}</li>)}
                                    </ul>
                                </div>
                            )}

                            {s.note && <p className="legal-note">ℹ️ {s.note}</p>}

                            {/* Subsections */}
                            {s.subsections && s.subsections.map((sub, si) => (
                                <div key={si} className="legal-subsection">
                                    <h3>{sub.title}</h3>
                                    {sub.content && <p className="legal-para">{sub.content}</p>}
                                    {sub.bullets && (
                                        <ul className="legal-bullets">
                                            {sub.bullets.map((b, i) => <li key={i}>✅ {b}</li>)}
                                        </ul>
                                    )}
                                    {sub.negatives && (
                                        <div className="legal-negatives">
                                            <span className="bunchy-label">Bunchy:</span>
                                            <ul>
                                                {sub.negatives.map((n, i) => <li key={i}>❌ {n}</li>)}
                                            </ul>
                                        </div>
                                    )}
                                    {sub.note && <p className="legal-note">ℹ️ {sub.note}</p>}
                                </div>
                            ))}
                        </section>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <footer className="legal-footer">
                <p>Questions? Contact us at <a href="mailto:support@bunchy.io">support@bunchy.io</a></p>
            </footer>
        </div>
    );
};

export default PrivacyPolicy;
